/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState } from "react";
import { useIntl, getLocale, useSelector } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";
import { Bubble } from "tea-component";
import {
  formatAccumulation, formatFilterValues,addLastDateRequestValue, formatPercentNum, formatNum, commonValueFormatter,
  generateCopyData, getUpAndDownStatus, getWeekDay
} from '@/pages/dashboard/utils/utils';
import {
  ICustomMetrics,
  IUseStatisticsDataFormatParams,
  IUseTableDataFormatParams,
  PagingQuery,
  SortBy,
  TableParamsType,
  columsType,
} from "./types";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from "@/pages/home/_config/config";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { IExplain } from "@/pages/dashboard/types/dashboard";
import { get } from 'lodash-es';
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { getDateFormatRangeString } from "@/pages/dashboard/components/OverallTableCo/hooks";
import { getMultiSortParams } from "@/pages/dashboard/components/OverallTableCo/utils";
import { getName } from "@home/utils/common";
import { dynamicString } from "@home/utils/dynamicString";
import dataCommonFormat from "@home/utils/dbDataFormat/dataCommonFormat";
import { resolveAndSelectName } from "@/pages/dashboard/utils/metricUtils";
import { ACCUMULATE_ALIGN_MAP, ACCUMULATE_WIDTH_MAP } from "@/pages/dashboard/_config/constant";

export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([]);
  // 总条数
  const [total, setTotal] = useState(0);

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);
  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  };
}

// 表格上传参数
export function getTableRequestParams({
  gameCode,
  filterValue: formVal,
  accumulated,
  sort,
  paging,
  selectType,
  haveLastDate,
  timeZone,
  options,
  ...resetParams }: TableParamsType) {
  const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
  const { rangeTime, timeType, diff, ...filters } = formVal || {};
  const {
    lockColumns = [],
    columns = [],
  } = options || {};
  const [start, end] = rangeTime;
  let params = {
    game_code: gameCode,
    filters: formatFilterValues(filters),
    accumulation: formatAccumulation(accumulated),
    page: paging.pageIndex,
    page_size: paging.pageSize,
    start_date: start,
    end_date: end,
    sorted_by: by?.replace(selectType, ''),
    desc: order === 'desc',
    multi_sort: getMultiSortParams(lockColumns, columns, sort, selectType, true),
    ...resetParams
  };
  params = addLastDateRequestValue(params, haveLastDate, timeZone);
  return params;
}

export function useTableColumns({ isAccumulated, hasDate, timeType, dateColumnConfig, accumulateColumnConfig }: { isAccumulated: boolean; hasDate: boolean; timeType: string; dateColumnConfig?: any; accumulateColumnConfig?: Record<string, { valuePrefix?: string; explainEn?: string; explainCn?: string; }>; }) {
  const { formatMessage } = useIntl();
  const [isIconHover, setIsIconHover] = useState(false);
  const isEn = getLocale() === 'en-US';
  const dataFrame = useSelector((state: any) => state?.dashboard?.currentGame?.data_frame || 'mobile');
  // 日期列, 周六日的颜色高亮,
  const defaultHeader = formatMessage({ id: "common_date" });
  const { headerEn, headerCn, explainEn: dateExplainEn, explainCn: dateExplainCn, width, fixed, align, widthByDate = 160, widthByRange = 220 } = dateColumnConfig || {};
  const configHeader = headerEn && headerCn 
    ? getName(isEn, headerEn, headerCn)
    : defaultHeader;
  const dateExplain = getName(isEn, dateExplainEn, dateExplainCn) || undefined;
  const defaultWidth = isAccumulated || (timeType === 'Week' && !hasDate) ? widthByRange : widthByDate;
  const DATE_COLUMN = {
    key: 'date',
    copyKey: 'date',
    header: configHeader,
    explain: dateExplain,
    width: width !== undefined ? dateColumnConfig.width : defaultWidth,
    fixed: fixed !== undefined ? dateColumnConfig.fixed : "left",
    align: align !== undefined ? dateColumnConfig.align : "left",
    render(record: { date: string; }) {
      const reg = /\((.+?)\)/gi;
      const weekValue = record?.date?.match(reg)?.[0]?.replace(/^\(/, "")?.slice(0, -1);
      let weekStr = '';
      if (weekValue) {
        if (weekValue?.includes('Null') || weekValue?.includes('去空')) {
          weekStr = `(${weekValue})`;
        } else {
          weekStr = weekValue;
        }
      }
      const dateVal = record?.date ? record?.date?.split('(') : ['-'];
      const isSunOrSat = weekStr === 'Sun' || weekStr === 'Sat' || weekStr === '周日' || weekStr === '周六';
      const showSumTip = record.date === 'Sum' || record.date === '汇总值';
      return <div className='dbd-overiew-table-date-columns-box'>
        <span className='table-text'>{dateVal?.[0]}</span>
        <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekStr}</span>
        {showSumTip && (
          <div className='port-item'>
            <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
              <Bubble
                dark
                placement="top-start"
                arrowPointAtCenter
                style={{ maxWidth: isEn ? '235px' : '253px' }}
                content={formatMessage({ id: 'overview_sum_tip' })}
              >
                <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
              </Bubble>
            </div>
          </div>
        )}
      </div>;
    }
  };
  // Accumulate 列
  const getAccumulateColumns = (accumulates: string[] = []) => {
    // if (accumulates[0] === ACCUMULATED_VALUE) return [];
    const columns: any[] = [];
    accumulates.forEach((value: string) => {
      const width = ACCUMULATE_WIDTH_MAP?.[value]?.[isEn ? 'en' : 'cn'] || 200;
      const columnConfig = accumulateColumnConfig?.[value];
      const valuePrefix = columnConfig?.valuePrefix;
      const accExplain = getName(isEn, columnConfig?.explainEn, columnConfig?.explainCn) || undefined;
      // @ts-ignore
      if (value !== 'date') {
        return columns.push({
          key: value,
          copyKey: value,
          header: formatMessage({ id: value === 'network_type' && dataFrame === 'casual' ? `common_casual_${value}` : `common_${value}` }),
          explain: accExplain,
          align: ACCUMULATE_ALIGN_MAP?.[value] || 'left',
          width,
          render(record: { dimension_name: { [x: string]: any; }; custom_dimensions?: Array<{ key: string; value: string; }>; }) {
            let displayValue: string;
            // 优先从 dimension_name 获取标准维度
            const standardValue = record.dimension_name?.[`${value}_name_${isEn ? 'en' : 'cn'}`];
            if (standardValue) {
              displayValue = standardValue;
            } else if (record.custom_dimensions && Array.isArray(record.custom_dimensions)) {
              const customDim = record.custom_dimensions.find((dim) => dim.key === value);
              displayValue = customDim?.value || "-";
            } else {
              displayValue = "-";
            }
            // 有数据且配置了 valuePrefix 时，在值前添加前缀
            if (valuePrefix && displayValue && displayValue !== "-") {
              return `${valuePrefix} ${displayValue}`;
            }
            return displayValue;
          }
        });
      }
    });
    return columns;
  };

  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (metricsKey: string[], metricsInfo: { [x: string]: any; }, explainInfo: IExplain, selectType: string, hideExplain = false, metricsData: any) => {
    const isShowUpDown = selectType !== "value_only";
    const metricKeyMap = buildMetricKeyMap(metricsInfo);
    return metricsKey.reduce((columns: columsType[], key: string) => {
      const info = metricsInfo[key];
      const { metricsConfig } = info || {};
      const { type, rainbowKey, enPercentWidth, cnPercentWidth, enWidth, cnWidth, enName, cnName, explainEn, explainCn, formatRule } = metricsConfig || {};
      const width = (isEn ? enPercentWidth : cnPercentWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      const onlyValueWidth = (isEn ? enWidth : cnWidth) + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT;
      const name = resolveAndSelectName(isEn, enName, cnName, metricsData?.metricsInfo) || '';
      
      // 兼容处理：如果没有 type 但有 formatRule，从 formatRule 推断 type
      let actualType = type;
      if (!actualType && formatRule) {
        const resolvedFormatRule: string = dynamicString(formatRule, metricsData);
        // 从 formatRule 推断是否为百分比类型，用来兼容之前的柱形图进度条逻辑
        if (resolvedFormatRule?.toLowerCase().includes('percent')) {
          actualType = 'percent';
        }
      }
      
      const isPercent = actualType === 'percent';
      // 兼容老的解释, 后续配置更新完可删除
      const explainOld = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      let explain = resolveAndSelectName(isEn, explainEn, explainCn, metricsData?.metricsInfo, undefined, explainOld) || '';
      // 如果 explain 为空或仍然包含模板字符串（未解析成功），则不显示浮窗
      if (!explain || (explain.includes('{{') && explain.includes('}}'))) {
        explain = undefined as any;
      }
      // @ts-ignore
      columns.push(
        {
          key: key + selectType,
          copyKey: key,
          selectType: selectType,
          header: name,
          align: "right",
          explain: hideExplain ? undefined : explain,
          width: isShowUpDown ? width : onlyValueWidth,
          isPercent: isPercent,
          render(record) {
            const metric = getMetricData(record?.metrics || {}, key, metricKeyMap) || {};
            const { progressWidth, metricValue } = metric;

            return (
              <>
                <div className={"table-text-bg"} style={{ width: `${progressWidth}%` }} />
                {/* <div className={'table-text-bg'} style={{width: `${progressWidth}%`}} /> */}
                <MetricAndUpDown
                  metric={metricValue}
                  updown={isShowUpDown ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                  hasTooltip={false}
                />
              </>);
          }
        },
      );
      return columns;
    }, []);
  };

  // 自定义列
  const getCustomColumns = (customColumns: ICustomMetrics[] = [], explainInfo: IExplain) => {
    const columns: any[] = [];
    customColumns?.forEach((item) => {
      const { columnKey, valueKey, rainbowKey, nameEn, nameCn, enWidth, cnWidth, formatRule, fillZero, emptyValue } = item;
      let explain = getExplainVal(explainInfo, rainbowKey, getLocale());
      // 如果 explain 为空或仍然包含模板字符串（未解析成功），则不显示浮窗
      if (!explain || (explain.includes('{{') && explain.includes('}}'))) {
        explain = undefined as any;
      }
      return columns.push({
        key: columnKey,
        copyKey: columnKey,
        header: isEn ? nameEn : nameCn,
        align: 'left',
        width: isEn ? enWidth : cnWidth,
        explain,
        render(record: any) {
          let value = get(record, valueKey);
          if (formatRule) {
            value = dataFormat({ value, formatRule, fillZero, emptyValue });
          }
          return value || "-";
        }
      });
    });
    return columns;
  };

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
    getCustomColumns
  };
}

/**
 * 复制当前列，语言切换，对应当前语言的取值
 * @param data 当前行数据
 */
const getCopyRenderValues = (data: any, extendConfig?: { gameCode?: any; selector?: any; }) => {
  const isEn = getLocale() === 'en-US';
  if (!extendConfig) {
    const { dimension_name: dimensionName, custom_dimensions: customDimensions } = data || {};
    if (dimensionName) {
      Object.keys(data).forEach(key => {
        const value = data[key];
        data[key] = dimensionName?.[`${key}_${isEn ? 'en' : 'cn'}`] || value;
      });
    }
    // 处理 custom_dimensions: 将其展开到顶层以便复制
    if (customDimensions && Array.isArray(customDimensions)) {
      customDimensions.forEach((dim: { key: string; value: string; }) => {
        if (!data[dim.key]) {
          data[dim.key] = dim.value;
        }
      });
    }
  } else {
    data = generateCopyData(data, { gameCode: extendConfig?.gameCode, selectorList: extendConfig.selector });
  }
  return data;
};

// 表格返回数据格式整理
export function useTableDataFormat(params: IUseTableDataFormatParams) {
  const { tableData, metricsInfo, timeType = "Day", isCopyData, extendConfig, metricsData } = params;
  const { formatMessage } = useIntl();
  return useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    const WEEK_DAY_ARR = [
      formatMessage({ id: 'common_mon' }),
      formatMessage({ id: 'common_tues' }),
      formatMessage({ id: 'common_wed' }),
      formatMessage({ id: 'common_thur' }),
      formatMessage({ id: 'common_fri' }),
      formatMessage({ id: 'common_sat' }),
      formatMessage({ id: 'common_sun' }),
    ];
    return tableData.map((data) => {
      if (isCopyData) {
        data = getCopyRenderValues(data, extendConfig);
      }
      const { date, metrics, type, custom_dimensions: customDimensions, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      let dateStr;

      if (date.includes('~')) {
        dateStr = getDateFormatRangeString(timeType, date);
      } else {
        dateStr = getDateFormatString(timeType, date);
      }

      dateStr = (timeType === "Month" || dateStr.includes('~')) ? dateStr : dateStr + getWeekDay(dateStr, WEEK_DAY_ARR);

      // 指标列数据
      const metricKeyMap = buildMetricKeyMap(metricsInfo);
      Object.keys(metricsInfo).forEach(key => {
        // 支持 metricKey → reqMetricKey 回退取值
        const rawMetric = getMetricData(metrics, key, metricKeyMap);
        const { value, dod, sdlw } = rawMetric || { value: "", dod: "", sdlw: "" };
        const { metricsConfig } = metricsInfo[key] || {};
        const { type, decimal, percentDecimals, dodFormatRule, sdlwFormatRule, formatRule, hideProgressBar = false } = metricsConfig;

        // 兼容处理：如果没有 type 但有 formatRule，从 formatRule 推断 type
        let actualType = type;
        if (!actualType && formatRule) {
          const resolvedFormatRule: string = dynamicString(formatRule, metricsData);
      
          // 从 formatRule 推断是否为百分比类型,用来兼容之前的柱形图进度条逻辑
          if (resolvedFormatRule?.toLowerCase().includes('percent')) {
          
            actualType = 'percent';
          }
        }

        const isPercent = actualType === 'percent';
        // 兼容 OverallTableCo 的逻辑：支持 hideProgressBar，且不限制 value > 0
        const progressWidth = isPercent && !hideProgressBar ? value * 100 : 0;
        // 优化：优先使用 formatRule，如果没有则使用旧的 type + decimal 逻辑
        let metricValue: string | number;
        if (formatRule) {
          // 使用 formatRule 格式化（需要先通过 dynamicString 处理模板字符串）
          const resolvedFormatRule: string = dynamicString(formatRule, metricsData);
          metricValue = dataCommonFormat({ value, formatRule: resolvedFormatRule });
        } else {
          // 兼容旧逻辑：使用 type + decimal
          metricValue = (key === 'accumulated_refund_rate' || key === 'lifetime_refund_rate') && value < 0 && isPercent 
            ? `-${formatPercentNum(value, decimal, false)}` 
            : commonValueFormatter(type, value, decimal);
        }
        
        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        
        // 优化：分开配置同比和环比的小数位，兼容旧逻辑
        // 如果 dodFormatRule 是字符串（格式化规则），使用 dataFormat；否则使用 percentDecimals 或默认值
        let dodPercentStr: string | number;
        if (dodFormatRule) {
          // const resolvedDodFormatRule = dynamicString(dodFormatRule, metricsData);
          dodPercentStr = dataCommonFormat({ value: dod, formatRule: dodFormatRule as string });
        } else {
          dodPercentStr = formatPercentNum(dod, percentDecimals ?? 1, true);
        }
        
        // 如果 sdlwFormatRule 是字符串（格式化规则），使用 dataFormat；否则使用 percentDecimals 或默认值
        let sdlwPercentStr: string | number;
        if (sdlwFormatRule) {
          // const resolvedSdlwFormatRule = dynamicString(sdlwFormatRule, metricsData);
          sdlwPercentStr = dataCommonFormat({ value: sdlw, formatRule: sdlwFormatRule as string });
        } else {
          sdlwPercentStr = formatPercentNum(sdlw, percentDecimals ?? 1, true);
        }
        
        metrics[key] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus,
          sdlwStatus,
          dodPercentStr,
          sdlwPercentStr,
          ...metrics[key]
        };
      });

      return {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: timeType === "Day" && (moment(date).format('E') === '6' || moment(date).format('E') === '7'),
        metrics,
        custom_dimensions: customDimensions, // 将 custom_dimensions 传递到行数据中
        ...accumulates
      };
    });
  }, [tableData, metricsInfo, timeType, isCopyData, extendConfig, metricsData, formatMessage, getLocale()]);
}

// 表格返回统计值数据格式整理 (和tableData用同一个hook时, 会因为useMemo调用次数太多而崩溃)
export function useStatisticsDataFormat(params: IUseStatisticsDataFormatParams) {
  const { statisticsData, metricsInfo, showStatistic, isCopyData, metricsData } = params;
  const { formatMessage } = useIntl();

  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {};
    // 构建 metricKey → reqMetricKey 映射，支持回退取值
    const metricKeyMap = buildMetricKeyMap(metricsInfo);
    return statisticsData.reduce((acc, data) => {
      const { date, metrics, type: statisticsType, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${statisticsType}` }); // 统计值行的数据, 日期列要展示对应的聚合标题
      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        // 支持 metricKey → reqMetricKey 回退取值
        const rawMetric = getMetricData(metrics, key, metricKeyMap);
        const { value } = rawMetric || { value: "", dod: "" };
        const { metricsConfig } = metricsInfo?.[key] || {};
        const { type, decimal, formatRule, hideProgressBar = false } = metricsConfig;
        let actualType = type;
        if (!actualType && formatRule) {
          const resolvedFormatRule: string = dynamicString(formatRule, metricsData);
          if (resolvedFormatRule?.toLowerCase().includes('percent')) {
            actualType = 'percent';
          }
        }
        const isPercent = actualType === 'percent';
        // const { isPercent, decimals, isUserType } = metricsInfo[key] // TODO 如果指标信息的配置对象格式不同, 导致逻辑无法复用
        const progressWidth = isPercent && statisticsType !== 'count' && value > 0 && !hideProgressBar ? value * 100 : 0; // 单元格内进度条的宽度
        
        // 优化：优先使用 formatRule，如果没有则使用旧的 type + decimal 逻辑
        let metricValue: string | number | undefined;
        if (isCopyData) {
          metricValue = value;
        } else if (statisticsType === 'count') {
          // count 类型统一使用整数格式，不使用 formatRule
          metricValue = formatNum(value, false, true);
        } else if (formatRule) {
          // 使用 formatRule 格式化（需要先通过 dynamicString 处理模板字符串）
          const resolvedFormatRule: string = dynamicString(formatRule, metricsData);
          // 保留原有特殊处理：accumulated_refund_rate 和 lifetime_refund_rate 为负值时，需要添加负号
          if ((key === 'accumulated_refund_rate' || key === 'lifetime_refund_rate') && value < 0 && isPercent) {
            const formattedValue = dataCommonFormat({ value: Math.abs(value), formatRule: resolvedFormatRule });
            metricValue = `-${formattedValue}`;
          } else {
            metricValue = dataCommonFormat({ value, formatRule: resolvedFormatRule });
          }
        } else {
          // 兼容旧逻辑：使用 type + decimal
          metricValue = statisticsType === 'count' 
            ? formatNum(value, false, true) 
            : (key === 'accumulated_refund_rate' || key === 'lifetime_refund_rate') && value < 0 && isPercent 
              ? `-${formatPercentNum(value, decimal, false)}` 
              : commonValueFormatter(type, value, decimal);
        }
        
        metrics[key] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue,
          dodStatus: "no",
          sdlwStatus: "no",
          dodPercentStr: "",
          sdlwPercentStr: "",
          ...metrics[key]
        };
      });

      acc[statisticsType] = {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: false,
        metrics,
        ...accumulates
      };
      return acc;
    }, {});
  }, [statisticsData, metricsInfo, showStatistic, isCopyData, metricsData, formatMessage, getLocale()]);
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension: string, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default:
      return moment(date).format("YYYY-MM-DD");
  }
};

export const useMetricsColumns = (configData?: any) => {
  const getColumn = (configData: any) => {
    const columnObj: any = {};
    configData.forEach((item: any, index: any) => {
      const { metricKey } = item || {};
      columnObj[metricKey] = {
        index: index,
        metricsConfig: item,
      };
    });
    return columnObj;
  };
  // 如果后端获取不到会拿前端的配置，所以这里不用写默认columns
  return getColumn(configData);
};

/**
 * 构建 metricKey → reqMetricKey 的映射表
 * 用于当 metricKey 与 reqMetricKey 不同时，从后端返回数据中回退取值
 */
export const buildMetricKeyMap = (metricsInfo: Record<string, any>): Record<string, string> => {
  const keyMap: Record<string, string> = {};
  Object.keys(metricsInfo).forEach((metricKey) => {
    const reqMetricKey = metricsInfo[metricKey]?.metricsConfig?.reqMetricKey;
    if (reqMetricKey && reqMetricKey !== metricKey) {
      keyMap[metricKey] = reqMetricKey;
    }
  });
  return keyMap;
};

/**
 * 从 metrics 数据中获取指标值，支持 metricKey → reqMetricKey 回退
 */
const getMetricData = (metrics: Record<string, any>, key: string, metricKeyMap: Record<string, string>) => {
  return metrics[key] || (metricKeyMap[key] ? metrics[metricKeyMap[key]] : undefined);
};


